import { FC, SubmitEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useUnsavedChanges } from '@connect-x-app/hybrid-state';
import { Role } from '@connect-x-app/shared-domain-models';
import { createReadableTextFromList, FormValidator, ValidatorResponse } from '@connect-x-app/shared-utils';
import { FormValidationErrors, useForm } from '@connect-x-app/web-state';
import { ADMIN_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Spinner, toastInfo } from '@connect-x-app/web-ui';
import { isEqual, omit } from 'lodash';

import { ModalForm } from '../../../components';
import { useGetRoles, useUpdateRole } from '../../../queries';
import { RoleForm } from './roleform/RoleForm';

type Props = {
  data: Role;
  isLoadingRole: boolean;
};

const validateForm = (values: Role): FormValidationErrors<Role> => {
  const validator = new FormValidator();

  return {
    // The validation logic needs a complete rethink, to also be done on change of the form
    airlineRole: validator.required(values.airlineRole),
    displayName: ((value: string): ValidatorResponse => {
      const requiredValidation = validator.required(value);
      if (!requiredValidation.isValid) {
        return requiredValidation;
      }

      const maxLengthValidation = validator.maxLength(value, 25);
      if (!maxLengthValidation.isValid) {
        return maxLengthValidation;
      }

      return validator.isAlphaNumeric(value);
    })(values.displayName),
  };
};

export const RoleDetails: FC<Props> = ({ data, isLoadingRole }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [sameDisplayNameRoles, setSameDisplayNameRoles] = useState<Role[]>([]);
  const { setHasUnsavedChanges } = useUnsavedChanges();
  const onSuccess = async () => {
    await setHasUnsavedChanges(false);

    navigate('/admin/connect/roles');
  };
  const { isPending: isUpdatingRole, mutate } = useUpdateRole(onSuccess);
  const { data: roles } = useGetRoles();

  const form = useForm<Role>({
    initialForm: data,
    submitForm: values => mutate(values),
    validateForm: values => validateForm(values),
  });

  useEffect(() => {
    setHasUnsavedChanges(form.isDirty);
  }, [form.isDirty, setHasUnsavedChanges]);

  const createRoleText = useCallback(
    (roles: Role[]) => {
      return createReadableTextFromList(
        roles.map(r => r.airlineRole),
        ', ',
        t('TAB.ROLES.LAST_SEPARATOR'),
        true,
      );
    },
    [t],
  );

  const onSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    const duplicateDisplayNameRoles = (roles ?? [])
      .filter(role => role.id !== form.values.id)
      .filter(role => role.displayName.toLowerCase() === form.values.displayName.toLowerCase());

    if (data.displayName.toLowerCase() !== form.values.displayName.toLowerCase() && duplicateDisplayNameRoles.length > 0) {
      event.preventDefault();
      form.setValues({ ...form.values, displayName: duplicateDisplayNameRoles[0].displayName });
      setSameDisplayNameRoles(duplicateDisplayNameRoles);
      return;
    }

    if (duplicateDisplayNameRoles.length > 0) {
      const formWithExistingDisplayName = { ...form.values, displayName: duplicateDisplayNameRoles[0].displayName };
      if (isEqual(omit(data, 'displayName'), omit(form.values, 'displayName'))) {
        event.preventDefault();
        toastInfo(t('ERRORS.ROLE.UPDATE.DISPLAY_NAME_CASING'));
        form.setValues(formWithExistingDisplayName);
        return;
      }

      form.submitWithParams(event, {
        submitForm: () => mutate(formWithExistingDisplayName),
      });
      return;
    }

    form.submit(event);
  };

  if (isUpdatingRole || isLoadingRole) {
    return <Spinner>{t('SHARED.LOADING_APPLICATION')}</Spinner>;
  }

  return (
    <main className="update-role-container">
      <RoleForm form={form} onSubmit={onSubmit} />
      {sameDisplayNameRoles?.length > 0 ? (
        <ModalForm
          disabled={false}
          headerTitle={t('ERRORS.WARNING')}
          isSubmitting={isUpdatingRole}
          onCancel={() => setSameDisplayNameRoles([])}
          onSubmit={form.submit}
          submitButtonText={t('SHARED.BUTTONS.PROCEED')}
          testId={ADMIN_TEST_IDS.roles.updateConfirmation}
        >
          {t(
            sameDisplayNameRoles?.length > 1
              ? 'TAB.ROLES.MODAL_CONFIRMATION.MULTIPLE_ROLES'
              : 'TAB.ROLES.MODAL_CONFIRMATION.ONE_ROLE',
            { users: createRoleText(sameDisplayNameRoles) },
          )}
        </ModalForm>
      ) : null}
    </main>
  );
};
